/**
 * なまえ(ランキング・リーグに出す表示名)。docs/12_challenge_ranking.md §1。
 *
 * ┌────────────────────────────────────┐
 * │ 🏷️ なまえ                              │
 * │ ランキングに でる なまえだよ              │
 * │ [ ぱるた ]            [ へんこうする ]   │
 * └────────────────────────────────────┘
 *   「へんこうする」を押すと、その場が入力欄に変わる(画面は移らない)。
 *   [ ________ ] 3/10もじ   [ けってい ] [ やめる ]
 *
 * 決まりごと:
 *   - 保存先は**サーバー**(`updateProfile` callable)。ランキングの根拠なので
 *     クライアントからは直接書けない(`firestore.rules` で deny)
 *   - **とうろく(ログイン)した子だけ**が変えられる。まだの子には
 *     「とうろく・ログインへ」の入口だけを出す(遊びの邪魔はしない)
 *   - 送信中は けってい を押せない(連打で2回送らない)。失敗したら
 *     **元の名前に戻して**その場でやさしく伝える(あとからこっそり直さない)
 *   - CLAUDE.md ルール7: グラデーションは使わない(フラットな単色)
 */
import { useEffect, useState } from 'react';
import {
  DEFAULT_DISPLAY_NAME,
  DISPLAY_NAME_MAX,
  displayNameError,
  displayNameLength,
  normalizeDisplayName,
} from '../services/profileName';
import { useAuthStore } from '../store/authStore';
import { useGameStore } from '../store/gameStore';
import './language.css';

/**
 * なまえ欄の出しかた。
 *
 *   `hidden`     … 欄ごと出さない(ローカルモード = とうろくの仕組みが無い)
 *   `signInFirst`… まだ とうろく していない。ログインへの入口だけ出す
 *   `edit`       … へんこうできる
 */
export type NameCardMode = 'hidden' | 'signInFirst' | 'edit';

export interface NameCardContext {
  /** とうろく・ログインが使えるか(ローカルモードでは false)。 */
  readonly authSupported: boolean;
  /** 匿名でない実アカウントで入っているか。 */
  readonly signedIn: boolean;
}

export function nameCardMode({ authSupported, signedIn }: NameCardContext): NameCardMode {
  if (!authSupported) return 'hidden';
  return signedIn ? 'edit' : 'signInFirst';
}

/** 順位表に出るときの見え方(未設定なら既定の「ぱるっこ」)。 */
export function displayedName(value: string | null): string {
  return value ?? DEFAULT_DISPLAY_NAME;
}

/** 送信の結果に対する、子どもに読める一言。 */
export const SAVE_FAILED_TEXT = 'いま ほぞんできなかったよ。もういちど ためしてね';

interface ViewProps {
  /** いまの なまえ(`null` = まだ つけていない)。 */
  readonly value: string | null;
  /** 送信中(けってい を押せなくする)。 */
  readonly saving: boolean;
  /** 保存する。成功したら true。 */
  readonly onSave: (value: string) => Promise<boolean>;
}

/** 見た目の本体(ストアを知らないので、そのまま組み合わせて試せる)。 */
export function NameSettingsView({ value, saving, onSave }: ViewProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [error, setError] = useState<string | null>(null);

  // 外から名前が入れ替わったら(保存が通ったら)入力欄も追随する。
  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  const start = (): void => {
    setDraft(value ?? '');
    setError(null);
    setEditing(true);
  };

  const cancel = (): void => {
    setEditing(false);
    setError(null);
    setDraft(value ?? '');
  };

  const commit = (): void => {
    if (saving) return;
    const reason = displayNameError(draft);
    if (reason !== null) {
      setError(reason);
      return;
    }
    void onSave(normalizeDisplayName(draft)).then((ok) => {
      if (ok) {
        setEditing(false);
        setError(null);
      } else {
        setError(SAVE_FAILED_TEXT);
      }
    });
  };

  const length = displayNameLength(normalizeDisplayName(draft));

  return (
    <section className="card language-settings name-settings">
      <h2 className="card-title">🏷️ なまえ</h2>
      <p className="lang-note">
        ランキングや リーグに でる なまえだよ。 ほんとうの なまえは いれないでね。
      </p>

      {editing ? (
        <>
          <div className="lang-choices name-row">
            <input
              className="name-input"
              type="text"
              name="display-name"
              autoComplete="off"
              aria-label="なまえ"
              value={draft}
              disabled={saving}
              onChange={(event) => {
                setDraft(event.target.value);
                setError(null);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') commit();
                if (event.key === 'Escape') cancel();
              }}
            />
            <span className={`name-count ${length > DISPLAY_NAME_MAX ? 'over' : ''}`}>
              {length}/{DISPLAY_NAME_MAX}もじ
            </span>
            <button type="button" className="name-ok" disabled={saving} onClick={commit}>
              {saving ? 'ほぞん ちゅう…' : 'けってい'}
            </button>
            <button type="button" className="name-cancel" disabled={saving} onClick={cancel}>
              やめる
            </button>
          </div>
          {error ? <p className="name-error">{error}</p> : null}
        </>
      ) : (
        <div className="lang-choices name-row">
          <p className="name-current">{displayedName(value)}</p>
          <button type="button" className="name-edit" onClick={start}>
            へんこうする
          </button>
        </div>
      )}
    </section>
  );
}

/** ログインしていない子に出す入口(欄の形はそのままで、中身だけ差し替える)。 */
export function NameSignInPrompt({ onGoAccount }: { readonly onGoAccount: () => void }) {
  return (
    <section className="card language-settings name-settings">
      <h2 className="card-title">🏷️ なまえ</h2>
      <p className="lang-note">
        なまえを つけると、ランキングや リーグに でるよ。 つけるには とうろく(ログイン)が
        ひつようだよ。
      </p>
      <div className="lang-choices name-row">
        <button type="button" className="name-edit" onClick={onGoAccount}>
          とうろく・ログインへ
        </button>
      </div>
    </section>
  );
}

/** 画面に置くほう。 */
export function NameSettings() {
  const displayName = useGameStore((state) => state.settings.displayName);
  const saving = useGameStore((state) => state.savingDisplayName);
  const saveDisplayName = useGameStore((state) => state.saveDisplayName);
  const goAccount = useGameStore((state) => state.goAccount);
  const authSupported = useAuthStore((auth) => auth.supported);
  const account = useAuthStore((auth) => auth.user);

  const mode = nameCardMode({
    authSupported,
    signedIn: account !== null && !account.isAnonymous,
  });

  if (mode === 'hidden') return null;
  if (mode === 'signInFirst') return <NameSignInPrompt onGoAccount={() => goAccount()} />;
  return <NameSettingsView value={displayName} saving={saving} onSave={saveDisplayName} />;
}
